import { Injectable } from '@angular/core'
import { APIService } from 'api/module'
import { TranslateService } from '@ngx-translate/core'
import { MediaFileBundle, MediaFileMini, MediaRoot } from 'elements-sdk'
import { HttpClient } from '@angular/common/http'
import { MessageBoxService } from 'common/util/module'
import { NotifyService } from 'common/util/services/notify.service'
import { FilesystemService } from 'storage/module'
import { noop } from 'rxjs'

@Injectable({ providedIn: 'root' })
export class MediaRenamingService {
  constructor (
    // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
    private api: APIService,
    private translate: TranslateService,
    private http: HttpClient,
    private messagebox: MessageBoxService,
    private notify: NotifyService,
    private filesystem: FilesystemService,
  ) { }

  async renameCustomFieldName (file: MediaFileMini, root: MediaRoot): Promise<void> {
    if (!file.customFields || !root.nameField) {
      return
    }

    const message = await this.messagebox.prompt({
      prompt: this.translate.instant('New name'),
      value: file.customFields[root.nameField],
    }).catch(() => null)
    if (!message?.value) {
      return
    }

    file.customFields[root.nameField] = message.value

    await this.api.mediaLibrary.patchMediaFile({
      id: file.id,
      mediaFilePartialUpdate: {
        customFields: file.customFields,
      },
      root: root.id,
    })
  }

  renameFile (file: MediaFileMini, root: MediaRoot): Promise<void> | undefined {
    if (root.nameField) {
      return this.renameCustomFieldName(file, root)
    } else {
      let oldExtension: string|null = null
      let oldName: string|null = null
      if (file.name.includes('.') && !file.isDir) {
        oldName = file.name.slice(0, file.name.lastIndexOf('.'))
        oldExtension = file.name.slice(file.name.lastIndexOf('.') + 1)
      } else {
        oldName = file.name
        oldExtension = null
      }

      return this.messagebox.prompt({
        prompt: this.translate.instant('New name (without extension)'),
        value: oldName,
      }).then(async (message) => {
        let newName = message.value
        if (!newName) {
          return
        }

        if (oldExtension) {
          newName += `.${oldExtension}`
        }

        try {
          await this.filesystem.renameFile(file.fullPath, newName)
        } catch (response) {
          this.notify.errorFromResponse(this.translate.instant('Error'), response)
        }
      }).catch(noop)
    }
  }

  async renameBundle (bundle: MediaFileBundle, root: MediaRoot): Promise<void> {
    if (root.nameField) {
      const mediaFile = await this.api.mediaLibrary.getMediaFile({ id: bundle.mainfile.id })
      await this.renameCustomFieldName(mediaFile, root)
      bundle.mainfile.customFields = mediaFile.customFields
    } else {
      if (bundle.fileCount > 1) {
        this.notify.warning(this.translate.instant('Cannot rename multi-file asset bundles'))
        return
      }
      if (bundle.fileCount === 0) {
        this.notify.warning(`${this.translate.instant('Cannot rename')} ${bundle.name}`)
        return
      }
      await this.renameFile(bundle.mainfile, root)
      bundle.name = bundle.mainfile.name
    }
  }
}
